import { useTranslation } from 'next-i18next';
import React, { useEffect, useState } from 'react';
import { Icon } from '@hotelplan/components.common.icon';
import { PlaceholderLoader } from '@hotelplan/components.common.placeholder-loader';
import { EPageType } from '@hotelplan/fdr.lib.page.available-pages';
import { getRouteByPageTypeLocale } from '@hotelplan/libs.router-config';
import {
  FdrImage,
  FdrLinkOpenMethod,
  FdrLinkType,
} from '@hotelplan/supergraph-api';
import { IBookmarkGroupState } from 'components/domain/fdr-bookmark/fdr-bookmark-types';
import useBookmarkGroupData from 'components/domain/fdr-bookmark/hooks/useBookmarkGroupData';
import FdrImgWithCopyright from 'components/domain/fdr-copyright/fdr-img-with-copyright';
import {
  FdrPdfButtonLink,
  ImageWrap,
  ModalCloseButton,
  PdfInfoTooltip,
  SearchDetailsContent,
  ShareOverlayWrap,
  ShareSocialNetworksList,
  TooltipWrap,
} from 'components/domain/fdr-share-overlay/fdr-share-overlay.styles';
import { EShareType } from 'components/domain/fdr-share-overlay/fdr-share-overlay.types';
import { useShareLinks } from 'components/domain/fdr-share-overlay/fdr-use-share-links';
import { Tooltip } from 'components/domain/fdr-site-tool/fdr-site-tool.styles';
import { ShareFormWrap } from 'components/domain/share-overlay/share-overlay.styles';
import ShareButton from 'components/domain/share-overlay/ShareButton';
import { fdrBookmarkPageDefaultPagination } from 'components/page/bookmark/bookmark.vars';
import { useFdrBookmarkGroupListCatalogIdsLazyQuery } from 'schemas/query/bookmark/fdr-bookmark-catalog-ids.generated';
import { FlaglineText, H4Heading } from 'theme/headings';
import { FdrSharePdfOverlaySkeleton } from './fdr-share-pdf-overlay.skeleton';

interface IFdrBookmarkCatalogOverlayProps {
  onClose?(): void;
  image: FdrImage;
  pageTitle: string;
  overlayTitle: string;
  activeGroup: IBookmarkGroupState;
  whiteList?: Array<EShareType>;
  trackFn(category: string, label: string, language: string): void;
  loading?: boolean;
}

const iconPlaceholder = (
  <PlaceholderLoader
    width={42}
    height={42}
    speed={2}
    style={{ width: `42px`, height: '42px' }}
    uid="iconPlaceholder"
  >
    <rect width={42} height={42} rx={8} />
  </PlaceholderLoader>
);

enum EOptions {
  WITH_PRICES = 'WITH_PRICES',
  WITHOUT_PRICES = 'WITHOUT_PRICES',
}

const FdrBookmarkCatalogOverlay: React.FC<IFdrBookmarkCatalogOverlayProps> = ({
  onClose,
  pageTitle,
  overlayTitle,
  whiteList,
  trackFn,
  loading,
  activeGroup,
}) => {
  const [t, { language }] = useTranslation('common');

  const { image } = useBookmarkGroupData(activeGroup.id);

  const OPTIONS: Record<EOptions, string> = {
    WITH_PRICES: t('share.overlay.button.pdfInfo.radioInput.yes'),
    WITHOUT_PRICES: t('share.overlay.button.pdfInfo.radioInput.no'),
  };

  const [selectedOption, setSelectedOption] = useState<EOptions>(
    EOptions.WITHOUT_PRICES
  );

  const [urlPdf, setUrlPdf] = useState<Record<EOptions, string | null>>({
    WITH_PRICES: null,
    WITHOUT_PRICES: null,
  });

  const [query, { loading: catalogIdLoading }] =
    useFdrBookmarkGroupListCatalogIdsLazyQuery();

  useEffect(() => {
    if (urlPdf[selectedOption] === null) {
      query({
        variables: {
          input: {
            pagination: fdrBookmarkPageDefaultPagination,
          },
          showPrice: selectedOption === EOptions.WITH_PRICES,
        },
      }).then(r => {
        const result = r.data?.fdrAllBookmarkGroups.entries?.find(
          entry => entry.id === activeGroup.id
        );
        const route = getRouteByPageTypeLocale(
          EPageType.OnDemandCatalog,
          language
        );

        const documentLocationOrigin =
          typeof document !== 'undefined' ? document.location.origin : '';

        setUrlPdf(prevState => {
          return {
            ...prevState,
            [selectedOption]: result
              ? `${documentLocationOrigin}${route.builder({
                  id: result.onDemandCatalogId,
                })}`
              : '',
          };
        });
      });
    }
  }, [selectedOption]);

  const toShare = urlPdf[selectedOption];
  const linksShare = useShareLinks(pageTitle, toShare, whiteList, true);

  const trackCopyOrShare = () => {
    trackFn(`Link kopieren`, selectedOption, language);
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedOption(event.target.value as EOptions);
  };

  if (activeGroup.items.length > 30) {
    return (
      <ShareOverlayWrap className="share-overlay">
        <FlaglineText className="tagline-text">{overlayTitle}</FlaglineText>
        <div
          className="alert"
          dangerouslySetInnerHTML={{
            __html: t('share.overlay.bookmark.alert'),
          }}
        ></div>
        <ModalCloseButton onClick={onClose}>{`Close window`}</ModalCloseButton>
      </ShareOverlayWrap>
    );
  }

  return (
    <ShareOverlayWrap className="share-overlay">
      <FlaglineText className="tagline-text">{overlayTitle}</FlaglineText>
      <SearchDetailsContent>
        <div className="radio-inputs-wrapper">
          <div className="pfd-inputs-title-wrapper">
            <h3 className="pfd-inputs-title">
              {t('share.overlay.button.pdf.inputsTitle')}
            </h3>
            <TooltipWrap>
              <Icon name="info" className="old-roundtrip" />
              <PdfInfoTooltip position={'bottom-left'}>
                {t('share.overlay.button.pdfInfo')}
              </PdfInfoTooltip>
            </TooltipWrap>
          </div>
          <div className="pdf-radio-inputs-wrapper">
            <label className="label">
              <input
                type="radio"
                value={EOptions.WITH_PRICES}
                checked={selectedOption === EOptions.WITH_PRICES}
                onChange={handleChange}
                className="input"
                name="pdf-options"
              />
              <span className="customRadioInput"></span>
              {OPTIONS.WITH_PRICES}
            </label>
            <label className="label">
              <input
                type="radio"
                value={EOptions.WITHOUT_PRICES}
                checked={selectedOption === EOptions.WITHOUT_PRICES}
                onChange={handleChange}
                className="input"
                name="pdf-options"
              />
              <span className="customRadioInput"></span>
              {OPTIONS.WITHOUT_PRICES}
            </label>
          </div>
        </div>
        <ImageWrap>
          {image ? (
            <FdrImgWithCopyright image={image} className="image" />
          ) : null}
        </ImageWrap>
        <H4Heading className="heading">{`[${t(
          'bookmark.bookmark.overlay.pdf.title'
        )}] ${pageTitle}`}</H4Heading>
      </SearchDetailsContent>
      {catalogIdLoading ? (
        <FdrSharePdfOverlaySkeleton />
      ) : (
        <>
          <div className="share-overlay-actions">
            {!Array.isArray(toShare) && (
              <ShareFormWrap
                url={toShare}
                onCopyButtonClick={trackCopyOrShare}
              />
            )}
            <ShareSocialNetworksList>
              {loading && (
                <div style={{ columnGap: '16px', display: 'flex' }}>
                  {Array.from({ length: whiteList.length }, (_, i) => (
                    <React.Fragment key={i}>{iconPlaceholder}</React.Fragment>
                  ))}
                </div>
              )}
              {!loading &&
                Object.keys(linksShare).map((platform, i) => {
                  if (!linksShare[platform]) return;
                  if (platform === 'download') return;

                  return (
                    <TooltipWrap key={i}>
                      <ShareButton
                        key={platform}
                        item={linksShare[platform]}
                        trackFn={() => {
                          trackFn(platform, selectedOption, language);
                        }}
                        platform={platform}
                      />
                      <Tooltip position={'bottom-left'} verticalOffsetPx={4}>
                        {t(linksShare[platform].tooltip)}
                      </Tooltip>
                    </TooltipWrap>
                  );
                })}
            </ShareSocialNetworksList>
          </div>
          <FdrPdfButtonLink
            link={{
              url: toShare,
              type: FdrLinkType.External,
              openMethod: FdrLinkOpenMethod.NewTab,
            }}
            onClick={() => {
              trackFn(`Download`, selectedOption, language);
            }}
          >
            {t('share.overlay.button.catalogDownload')}
          </FdrPdfButtonLink>
        </>
      )}
      <ModalCloseButton onClick={onClose}>{`Close window`}</ModalCloseButton>
    </ShareOverlayWrap>
  );
};

export default FdrBookmarkCatalogOverlay;
